import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
const uuidSchema = z.string().uuid('Invalid UUID format');
// --- Request Schemas ---
/**
 * Per-entry duration cap. The client timer flushes every 60s, pauses on idle (3min)
 * and on tab-hide, so legitimate entries are ≤ ~3min. 15min is a generous sanity
 * backstop — anything larger indicates a bug (clock skew, idle-detection failure)
 * and should be rejected rather than silently absorbed.
 */
export const STEP_TIME_MAX_DURATION_MS = 15 * 60 * 1000;
/**
 * Tolerance applied when checking that active duration doesn't exceed wall-clock.
 * Covers small clock skew between `performance.now()` (used for durationMs) and
 * `Date.now()` (used for the started_at/ended_at timestamps).
 */
export const STEP_TIME_WALL_CLOCK_TOLERANCE_MS = 5000;
export const StepTimeEntrySchema = z
    .object({
    stepId: uuidSchema.openapi({
        description: 'UUID of the step the learner spent time on',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    durationMs: z
        .number()
        .int()
        .positive()
        .max(STEP_TIME_MAX_DURATION_MS)
        .openapi({
        description: `Active time spent on the step in milliseconds (max ${STEP_TIME_MAX_DURATION_MS / 60000} minutes)`,
        example: 45000,
    }),
    startedAt: z.string().datetime().openapi({
        description: 'ISO 8601 timestamp when the learner started viewing the step',
        example: '2026-02-27T10:00:00.000Z',
    }),
    endedAt: z.string().datetime().openapi({
        description: 'ISO 8601 timestamp when the learner stopped viewing the step',
        example: '2026-02-27T10:00:45.000Z',
    }),
    phase: z
        .enum(['reading', 'answering', 'reviewing'])
        .optional()
        .openapi({
        description: 'Time tracking phase: reading (entry → first interaction), answering (first interaction → submit), reviewing (grading shown → leave step)',
        example: 'answering',
    }),
    visitNumber: z.number().int().positive().openapi({
        description: 'Sequential visit number for this step within the session',
        example: 1,
    }),
    clientEntryId: uuidSchema.optional().openapi({
        description: 'Optional client-generated UUID for deduplication',
        example: '660e8400-e29b-41d4-a716-446655440001',
    }),
})
    .refine((entry) => {
    const started = Date.parse(entry.startedAt);
    const ended = Date.parse(entry.endedAt);
    if (Number.isNaN(started) || Number.isNaN(ended))
        return false;
    if (ended < started)
        return false;
    // Active time cannot exceed wall-clock window between started_at and ended_at
    // (allowing a small tolerance for clock-skew between perf.now and Date.now).
    return entry.durationMs <= ended - started + STEP_TIME_WALL_CLOCK_TOLERANCE_MS;
}, {
    message: 'durationMs must not exceed (endedAt - startedAt) and endedAt must be >= startedAt',
});
export const FlushStepTimeBodySchema = z.object({
    entries: z.array(StepTimeEntrySchema).min(1).max(50).openapi({
        description: 'Array of step time entries to flush (1–50 entries per request)',
    }),
});
export const FlushStepTimeParamSchema = z.object({
    sessionId: uuidSchema.openapi({
        description: 'UUID of the learning session to flush time entries for',
        example: '770e8400-e29b-41d4-a716-446655440002',
    }),
});
// --- Response Schemas ---
export const DailyLearningTimeSchema = z
    .object({
    date: z.string().openapi({ example: '2026-02-24' }),
    dayLabel: z.string().openapi({ example: 'Mon' }),
    totalMs: z.number().openapi({ example: 3600000 }),
})
    .openapi('DailyLearningTime');
export const HourlyLearningTimeSchema = z
    .object({
    hour: z.string().openapi({ example: '2pm' }),
    totalMs: z.number().openapi({ example: 300000 }),
})
    .openapi('HourlyLearningTime');
export const LearningTimeResponseSchema = z
    .object({
    totalMs: z.number().openapi({
        description: 'Total learning time in milliseconds for the period',
        example: 18000000,
    }),
    previousPeriodMs: z.number().openapi({
        description: 'Total learning time in milliseconds for the equal-length prior window',
        example: 12000000,
    }),
    dailyBreakdown: z.array(DailyLearningTimeSchema).openapi({
        description: 'Daily learning time buckets',
    }),
    todayMs: z.number().openapi({
        description: 'Total learning time in milliseconds for today in user timezone',
        example: 3600000,
    }),
    hourlyBreakdown: z.array(HourlyLearningTimeSchema).openapi({
        description: 'Learning time per hour for the last 7 clock hours (may span midnight)',
    }),
})
    .openapi('LearningTimeResponse');
export const CourseLearningTimeResponseSchema = z
    .object({
    totalMs: z.number().openapi({
        description: 'Total learning time in milliseconds for the course',
        example: 3600000,
    }),
    weeklyBreakdown: z.array(DailyLearningTimeSchema).openapi({
        description: 'Learning time per day for the current week',
    }),
})
    .openapi('CourseLearningTimeResponse');
//# sourceMappingURL=step-time.schema.js.map